/**
 * Deciding a lead from the desk.
 *
 * The first write the browser makes, and deliberately the thinnest thing that
 * can make it. Which statuses may be decided, what a reopening does to the
 * ledger entry, and whether this caller may decide anything at all are settled
 * in Python and refused there — a button that should not exist answers `403`
 * rather than doing something.
 *
 * What this owns is what a seam owns: carrying the credential, refetching the
 * lists the decision changed, and letting the service's own sentence reach the
 * reviewer instead of a generic failure.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { credentialHeaders, useCredential } from '../lib/credential';
import type { Company, Exclusion, ResearchCandidate } from '../lib/types';

/**
 * Lists a decision makes stale.
 *
 * Named once rather than at each call site: a decision moves a lead *and* its
 * ledger entry, and a hook that refetched only the list it happened to be
 * rendered beside would leave the other showing the state the reviewer just
 * left — which reads as the decision having failed, and invites making it
 * twice.
 */
const CHANGED_BY_A_DECISION = [['research'], ['exclusions'], ['dashboard']] as const;

/**
 * What a reviewer states when ruling a lead out.
 *
 * No name. The route is addressed by candidate and the service keys the ledger
 * on the lead's own name — a client free to state a different one wrote an
 * entry belonging to no lead, which is the shape `LeadExclusionCreate` exists
 * to make unstatable.
 */
export type ExclusionDraft = {
  reasonCode: string;
  rationale: string;
  reconsiderationTrigger: string;
};

export function useExcludeLead(candidateId: string) {
  const { key } = useCredential();
  const client = useQueryClient();
  return useMutation<Exclusion, Error, ExclusionDraft>({
    mutationFn: (draft: ExclusionDraft) =>
      api<Exclusion>(`/api/research-candidates/${candidateId}/exclusion`, {
        method: 'POST',
        headers: { ...credentialHeaders(key), 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      }),
    onSuccess: () => {
      for (const queryKey of CHANGED_BY_A_DECISION) {
        void client.invalidateQueries({ queryKey });
      }
    },
    retry: false,
  });
}

/**
 * The registry record a lead becomes.
 *
 * A partial `CompanyCreate`: the schema requires only `name` and supplies a
 * default for the other twenty-five. Which of those the form must state rather
 * than inherit is a measured question, answered in `PromoteForm`.
 */
export type CompanyDraft = Record<string, unknown>;

export function usePromoteLead(candidateId: string) {
  const { key } = useCredential();
  const client = useQueryClient();
  return useMutation<Company, Error, CompanyDraft>({
    mutationFn: (draft: CompanyDraft) =>
      api<Company>(`/api/research-candidates/${candidateId}/promotion`, {
        method: 'POST',
        headers: { ...credentialHeaders(key), 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      }),
    onSuccess: () => {
      // A promotion moves a lead and adds a company, so the registry listing is
      // stale too — the one list a decision changes that the other two do not.
      for (const queryKey of [...CHANGED_BY_A_DECISION, ['companies']]) {
        void client.invalidateQueries({ queryKey });
      }
    },
    retry: false,
  });
}

export function useReopenLead() {
  const { key } = useCredential();
  const client = useQueryClient();
  return useMutation<ResearchCandidate, Error, string>({
    mutationFn: (candidateId: string) =>
      api<ResearchCandidate>(`/api/research-candidates/${candidateId}/reconsideration`, {
        method: 'POST',
        headers: credentialHeaders(key),
      }),
    // On success only. Refetching after a refusal would change the screen for a
    // reason unrelated to the decision, which is indistinguishable from it
    // having worked.
    onSuccess: () => {
      for (const queryKey of CHANGED_BY_A_DECISION) {
        void client.invalidateQueries({ queryKey });
      }
    },
    retry: false,
  });
}
